import { Box, Typography } from '@mui/material';
import { useEffect, useRef, type SyntheticEvent } from 'react';

import VNRichtextDisplay from '@/components/VNRichtext/VNRichtextDisplay';

export type NotificationBodyFormat = 'html' | 'richtext';

interface NotificationBodyContentProps {
  body?: string | null;
  /** `html` renders in a sandboxed iframe; `richtext` renders markdown to match the editor. */
  format: NotificationBodyFormat;
  /** When true, the HTML iframe auto-sizes to its content height (hub detail pane). */
  bodyOnly?: boolean;
  /** When true, HTML iframe grows to document height so a parent scroll pane can scroll long content. */
  autoSizeHtml?: boolean;
}

function measureIframeDocumentHeight(iframe: HTMLIFrameElement): number {
  try {
    const doc = iframe.contentDocument;
    if (!doc) {
      return 0;
    }
    const body = doc.body;
    const el = doc.documentElement;
    return Math.max(
      body?.scrollHeight ?? 0,
      body?.offsetHeight ?? 0,
      el?.scrollHeight ?? 0,
      el?.offsetHeight ?? 0,
      1
    );
  } catch {
    return 0;
  }
}

/** Prevent authored HTML from creating an inner scrollport when the parent pane scrolls. */
function disableIframeDocumentScroll(iframe: HTMLIFrameElement) {
  try {
    const doc = iframe.contentDocument;
    if (!doc) {
      return;
    }
    if (doc.documentElement) {
      doc.documentElement.style.overflow = 'hidden';
    }
    if (doc.body) {
      doc.body.style.overflow = 'hidden';
    }
  } catch {
    // ignore cross-origin / unavailable document
  }
}

/**
 * Renders a notification body in the format it was authored in. HTML bodies are shown in a
 * sandboxed iframe (user-activated links may navigate the top window via `target="_top"`);
 * richtext (markdown) bodies are rendered with the same prose baseline as the `VNRichtext`
 * editor (see the `vn-prose` mixin in `vn-richtext.scss`) so display matches editing.
 */
const NotificationBodyContent = ({
  body,
  format,
  bodyOnly = false,
  autoSizeHtml = false,
}: NotificationBodyContentProps) => {
  const content = String(body ?? '');
  const hasBody = Boolean(body);
  const shouldAutoSizeHtml = bodyOnly || autoSizeHtml;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const applyAutoSize = (iframe: HTMLIFrameElement) => {
    if (!shouldAutoSizeHtml) {
      return;
    }
    const docH = measureIframeDocumentHeight(iframe);
    if (docH <= 0) {
      return;
    }
    // +1px buffer avoids clipping the last line once iframe scroll is disabled.
    const next = `${docH + 1}px`;
    // Skip no-op writes — setting height every RO tick causes ResizeObserver loops.
    if (iframe.style.height === next) {
      return;
    }
    iframe.style.height = next;
  };

  useEffect(() => {
    if (!shouldAutoSizeHtml || format !== 'html') {
      return;
    }
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    let mo: MutationObserver | null = null;
    let raf = 0;
    const scheduleAutoSize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        disableIframeDocumentScroll(iframe);
        applyAutoSize(iframe);
      });
    };

    const bindDocObservers = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) {
          return;
        }
        disableIframeDocumentScroll(iframe);
        doc.querySelectorAll('img').forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', scheduleAutoSize, { once: true });
          }
        });
        // Prefer MutationObserver over ResizeObserver — RO on the iframe body loops when we set height.
        mo = new MutationObserver(scheduleAutoSize);
        mo.observe(doc.body, { childList: true, subtree: true, characterData: true });
        scheduleAutoSize();
      } catch {
        // ignore
      }
    };

    const onLoad = () => {
      disableIframeDocumentScroll(iframe);
      scheduleAutoSize();
      bindDocObservers();
    };
    iframe.addEventListener('load', onLoad);

    if (iframe.contentDocument?.readyState === 'complete') {
      bindDocObservers();
    }

    return () => {
      cancelAnimationFrame(raf);
      iframe.removeEventListener('load', onLoad);
      mo?.disconnect();
    };
  }, [shouldAutoSizeHtml, format, content]);

  const handleIframeLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
    if (!shouldAutoSizeHtml) {
      return;
    }
    disableIframeDocumentScroll(event.currentTarget);
    applyAutoSize(event.currentTarget);
  };

  if (!hasBody) {
    return (
      <Box className="notification-body__state">
        <Typography variant="body2" color="text.secondary">
          No body content yet.
        </Typography>
      </Box>
    );
  }

  if (format === 'html') {
    return (
      <iframe
        ref={iframeRef}
        className={`notification-body__iframe${shouldAutoSizeHtml ? ' notification-body__iframe--auto-size' : ''}`}
        title="Notification body"
        srcDoc={content}
        sandbox="allow-same-origin allow-top-navigation-by-user-activation"
        scrolling={shouldAutoSizeHtml ? 'no' : undefined}
        onLoad={handleIframeLoad}
      />
    );
  }

  return (
    <div className="notification-body__markdown">
      <VNRichtextDisplay markdown={content} />
    </div>
  );
};

export default NotificationBodyContent;
